import React, { useState, useRef, useCallback } from 'react';
import { FadeIn } from 'rad-ui-package';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import PhaseA from './PhaseA';
import PhaseB from './PhaseB';
import PhaseC from './PhaseC';
import ProcessingStatus from './ProcessingStatus';
import API_URL from '../config';
import './AgentBehaviorMaker.css';

// Fetch with timeout — prevents hanging requests
const fetchWithTimeout = (url, options, timeoutMs = 90000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

const MAX_CONVERSATIONS = 20;

const AgentBehaviorMaker = () => {
  const [documents, setDocuments] = useState({
    productProposal: null,
    userPersona: null,
    agentFramework: null
  });

  const [processedData, setProcessedData] = useState(null); // Store extracted reasoning
  const [scenarios, setScenarios] = useState([]);
  const [agentControls, setAgentControls] = useState({
    tone: 'professional',
    formality: 50,
    verbosity: 50,
    empathy: 70,
    proactivity: 50,
    creativity: 50,
    technicalDepth: 50
  });

  const [conversations, setConversations] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const controlsDebounceRef = useRef(null);
  const agentControlsRef = useRef(agentControls);
  agentControlsRef.current = agentControls;
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const controlsRefreshIdRef = useRef(0);
  /** null | 'queued' (debouncing) | 'running' (API in flight) */
  const [controlsRefreshPhase, setControlsRefreshPhase] = useState(null);

  const handleDocumentsSubmit = async (docs) => {
    setIsProcessing(true);
    setDocuments(docs);
    setProcessingStatus({ currentStep: 'reading', message: 'Reading your documents...' });
    
    try {
      // Step 1: Reading (already done in PhaseA)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setProcessingStatus({ currentStep: 'extracting', message: 'Extracting key information...' });

      // Step 1: Extract (single Bedrock call, ~15-25s) — under 29s API Gateway limit
      const extractRes = await fetchWithTimeout(`${API_URL}/api/abm/process-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(docs)
      });
      let extractData;
      try {
        const text = await extractRes.text();
        extractData = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        throw new Error(
          extractRes.status === 502 || extractRes.status === 504
            ? 'Extraction timed out. Try shorter documents or request API Gateway timeout increase in Service Quotas.'
            : `Invalid response from server (${extractRes.status})`
        );
      }
      if (!extractRes.ok) {
        throw new Error(extractData.error || 'Failed to extract from documents');
      }

      const processedDocs = extractData.processedData;
      setProcessedData(processedDocs);
      setProcessingStatus({ currentStep: 'generating', message: 'Generating scenarios...' });

      // Step 2: Generate scenarios (single Bedrock call, ~10-20s) — under 29s limit
      const scenariosRes = await fetchWithTimeout(`${API_URL}/api/abm/generate-scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ processedData: processedDocs })
      });
      let scenariosData;
      try {
        const text = await scenariosRes.text();
        scenariosData = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        throw new Error(
          scenariosRes.status === 502 || scenariosRes.status === 504
            ? 'Scenario generation timed out. Try again.'
            : `Invalid response from server (${scenariosRes.status})`
        );
      }
      if (!scenariosRes.ok) {
        throw new Error(scenariosData.error || 'Failed to generate scenarios');
      }

      setProcessingStatus({ currentStep: 'complete', message: 'All done!' });
      setScenarios(scenariosData.scenarios || []);
      
      // Apply AI-suggested control defaults based on document analysis
      if (extractData.suggestedControls) {
        const sc = extractData.suggestedControls;
        setAgentControls(prev => ({
          tone: sc.tone || prev.tone,
          formality: typeof sc.formality === 'number' ? sc.formality : prev.formality,
          verbosity: typeof sc.verbosity === 'number' ? sc.verbosity : prev.verbosity,
          empathy: typeof sc.empathy === 'number' ? sc.empathy : prev.empathy,
          proactivity: typeof sc.proactivity === 'number' ? sc.proactivity : prev.proactivity,
          creativity: typeof sc.creativity === 'number' ? sc.creativity : prev.creativity,
          technicalDepth: typeof sc.technicalDepth === 'number' ? sc.technicalDepth : prev.technicalDepth,
        }));
      }
      
      // Drop raw document content from state — downstream calls only need processedData.
      // Keep a lightweight reference so PhaseB/PhaseC know docs were submitted.
      setDocuments({
        productProposal: { type: docs.productProposal.type, submitted: true },
        userPersona: { type: docs.userPersona.type, submitted: true },
        agentFramework: { type: docs.agentFramework.type, submitted: true }
      });
      
      // Clear status after a moment
      setTimeout(() => setProcessingStatus(null), 2000);
    } catch (error) {
      console.error('Error processing documents:', error);
      let message = error.message;
      if (error.name === 'AbortError') {
        message = 'Request timed out. Try shorter documents or request API Gateway timeout increase in AWS Service Quotas.';
      } else if (/too many requests|throttl|rate.?limit/i.test(message || '')) {
        message = 'The AI service is rate-limited. Please wait 30-60 seconds and try again. If this persists, your AWS Bedrock account may need a quota increase (Service Quotas → Bedrock → Claude on-demand tokens/minute).';
      } else if (message === 'Load failed' || (error.message || '').includes('Load failed')) {
        message = 'Network request failed. Often caused by API Gateway 29s timeout—request quota increase in Service Quotas (API Gateway → Maximum integration timeout), or try shorter documents.';
      } else if (!message) {
        message = 'An unexpected error occurred. Please try again.';
      }
      setProcessingStatus({ 
        currentStep: 'error', 
        error: message,
        details: { error: error.toString() }
      });
      // Don't clear error status automatically
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUserInterrupt = async (userInput) => {
    if (!userInput.trim()) return;
    setIsProcessing(true);
    setIsConversationLoading(true);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/abm/user-interrupt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: userInput,
          currentConversation: conversations[conversations.length - 1],
          processedData,
          agentControls
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Server returned ${response.status}`);
      }
      if (data.conversation) {
        setConversations(prev => [...prev, data.conversation].slice(-MAX_CONVERSATIONS));
      }
    } catch (error) {
      console.error('Error processing user input:', error);
      const msg = /too many requests|throttl|rate.?limit/i.test(error.message || '')
        ? 'The AI service is rate-limited. Wait 30-60 seconds and try again.'
        : error.message || 'Failed to process input.';
      setProcessingStatus({ currentStep: 'error', error: msg });
    } finally {
      setIsProcessing(false);
      setIsConversationLoading(false);
    }
  };

  const handleScenarioSelect = async (scenario) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setIsConversationLoading(true);
    
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/abm/generate-conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenario,
          processedData,
          agentControls
        })
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Server returned ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.conversation) {
        setConversations(prev => [...prev, data.conversation].slice(-MAX_CONVERSATIONS));
      } else {
        console.warn('No conversation in response:', data);
      }
    } catch (error) {
      console.error('Error generating conversation:', error);
      const msg = /too many requests|throttl|rate.?limit/i.test(error.message || '')
        ? 'The AI service is rate-limited. Wait 30-60 seconds and try again.'
        : error.message || 'Failed to generate conversation.';
      setProcessingStatus({ currentStep: 'error', error: msg });
    } finally {
      setIsProcessing(false);
      setIsConversationLoading(false);
    }
  };

  const handleControlsChange = (newControls) => {
    // Merge with ref so rapid changes (before re-render) don't lose previous edits
    const merged = { ...agentControlsRef.current, ...newControls };
    agentControlsRef.current = merged;
    setAgentControls(merged);

    if (controlsDebounceRef.current) {
      clearTimeout(controlsDebounceRef.current);
    }

    if (conversationsRef.current.length > 0) {
      controlsRefreshIdRef.current += 1;
      const refreshId = controlsRefreshIdRef.current;
      setControlsRefreshPhase('queued');

      controlsDebounceRef.current = setTimeout(async () => {
        const controlsToUse = agentControlsRef.current;
        setControlsRefreshPhase('running');
        setIsProcessing(true);
        setIsConversationLoading(true);
        try {
          const response = await fetchWithTimeout(`${API_URL}/api/abm/refresh-conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              processedData,
              agentControls: controlsToUse,
              existingConversations: conversationsRef.current
            })
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || `Server returned ${response.status}`);
          }
          setConversations(data.conversations);
        } catch (error) {
          console.error('Error refreshing conversations:', error);
          const msg = /too many requests|throttl|rate.?limit/i.test(error.message || '')
            ? 'The AI service is rate-limited. Wait 30-60 seconds and try again, or slow down control changes.'
            : error.message || 'Failed to refresh conversation.';
          setProcessingStatus({ currentStep: 'error', error: msg });
        } finally {
          setIsProcessing(false);
          setIsConversationLoading(false);
          if (refreshId === controlsRefreshIdRef.current) {
            setControlsRefreshPhase(null);
          } else {
            setControlsRefreshPhase('queued');
          }
        }
      }, 1500); // debounce: wait 1.5s after last change to avoid rapid-fire API calls
    }
  };

  const handleExportFramework = async () => {
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/abm/export-framework`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processedData,
          agentControls,
          conversations
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: `Export failed (${response.status})` }));
        throw new Error(err.error || 'Export failed');
      }

      const { personaDoc, steeringDoc } = await response.json();
      if (!personaDoc || !steeringDoc) {
        throw new Error('Invalid export response');
      }

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      zip.file('updated-persona.md', personaDoc);
      zip.file('ai-persona-steering.md', steeringDoc);
      const blob = await zip.generateAsync({ type: 'blob' });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'agent-persona-export.zip';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting framework:', error);
      setProcessingStatus({ currentStep: 'error', error: error.message });
    }
  };

  return (
    <FadeIn>
      <div className="agent-behavior-maker">
        <div className="abm-header">
          <h1>🤖 Agent Behavior Maker</h1>
          <Box color="text-body-secondary" fontSize="body-m">Design and test LLM behaviors for your product</Box>
        </div>

        {processingStatus && <ProcessingStatus status={processingStatus} compact />}

        {controlsRefreshPhase && (
          <div
            className="abm-controls-refresh-chip"
            role="status"
            aria-live="polite"
            aria-busy={controlsRefreshPhase === 'running'}
          >
            <StatusIndicator type={controlsRefreshPhase === 'running' ? 'loading' : 'in-progress'}>
              {controlsRefreshPhase === 'queued'
                ? 'Controls changed — updating preview…'
                : 'Regenerating conversation…'}
            </StatusIndicator>
          </div>
        )}

        <div className="abm-container">
          <PhaseA 
            onSubmit={handleDocumentsSubmit}
            isProcessing={isProcessing}
          />
          
          <PhaseB 
            scenarios={scenarios}
            conversations={conversations}
            onScenarioSelect={handleScenarioSelect}
            onUserInterrupt={handleUserInterrupt}
            isProcessing={isProcessing}
            isConversationLoading={isConversationLoading}
            documents={documents}
            processedData={processedData}
            isLoadingScenarios={!!processingStatus && processingStatus.currentStep !== 'error' && processingStatus.currentStep !== 'complete'}
          />
          
          <PhaseC 
            controls={agentControls}
            onChange={handleControlsChange}
            onExport={handleExportFramework}
            isProcessing={isProcessing}
            hasDocuments={!!documents.productProposal}
          />
        </div>
      </div>
    </FadeIn>
  );
};

export default AgentBehaviorMaker;
